import { useLocation } from '@reach/router';
import { AxiosError } from 'axios';
import { navigate } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formPost } from 'src/utils/clientRequests';
import {
  validateEmailPattern,
  validatePostalCodePattern,
  validateRequired,
  validateRequiredEmail,
  validateRequiredFirstName,
  validateRequiredLastName,
  validateRequiredPostalCode,
  validateDateRange
} from 'src/utils/formHelpers';
import RichText from '../rich_text/RichText';

interface FormValues {
  date: string;
  firstName: string;
  lastName: string;
  petName: string | 'Kitten' | 'Puppy';
  petBirthday: string;
  breedSize: string;
  petGoHomeDate: string;
  petType: 'CAT' | 'DOG';
  email: string;
  postalCode: string;
  locale: {
    keyName: Languages;
  };
  agreed: boolean;
  breederId: string | null;
}

interface BreederAdoptionFormProps {
  getFieldDataByName: Function;
  langcode: Languages;
  petType: 'CAT' | 'DOG';
  petName: 'Kitten' | 'Puppy';
  webformId: string;
  formOptIn?: string;
}

const BreederAdoptionForm = ({
  getFieldDataByName,
  langcode,
  petType,
  petName,
  webformId,
  formOptIn,
}: BreederAdoptionFormProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const breederId = queryParams.get('breeder_id');

  // The breeder_id is required. If it is missing, redirect to breeder page.
  useEffect(() => {
    if (breederId === null || breederId === '' || breederId.trim() === '') {
      // if parameter is missing, empty, or just whitespace
      navigate('/find-a-dog-breeder');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date().toISOString().slice(0, 10),
      firstName: '',
      lastName: '',
      petName: '',
      petBirthday: new Date().toISOString().slice(0, 10),
      breedSize: '',
      petGoHomeDate: new Date().toISOString().slice(0, 10),
      petType,
      email: '',
      postalCode: '',
      locale: {
        keyName: 'en',
      },
      agreed: false,
      breederId,
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [apiError, setApiError] = useState('');
  const apiRef = useRef<HTMLDivElement>(null);

  // Pet birthdate min, max validation.
  const minToday = new Date();
  const minusYear = new Date(minToday.setFullYear(minToday.getFullYear() - 2));
  const maxToday = new Date();
  const plusYear = new Date(maxToday.setFullYear(maxToday.getFullYear() + 1));
  const minBirthDate: string = minusYear.toISOString().split('T')[0];
  const maxBirthDate: string = plusYear.toISOString().split('T')[0];

  const onSubmit: SubmitHandler<FormValues> = async formData => {
    setSubmitting(true);
    const formattedData = {
      webform_id: webformId,
      date: formData.date,
      first_name: formData.firstName,
      last_name: formData.lastName,
      pet_name: formData.petName === '' ? petName : formData.petName,
      pet_birthday: formData.petBirthday,
      pet_type: formData.petType,
      email_address: formData.email,
      postal_code: formData.postalCode,
      language: langcode || 'en',
      agree_box: formData.agreed,
      breeder_id: breederId,
      pet_go_home_date: formData.petGoHomeDate,
      ...(petType === 'DOG' && { breed_size: formData.breedSize }),
    };
    const breederAdoptionResponse = await formPost({ data: formattedData });

    if (!breederAdoptionResponse) {
      setApiError('Something went wrong');
      setSubmitting(false);
      setTimeout(() => {
        apiRef.current?.focus();
      }, 500);
      return;
    }

    if (breederAdoptionResponse instanceof AxiosError) {
      setApiError(breederAdoptionResponse.response?.data?.message || 'Something went wrong');
      setSubmitting(false);
      setTimeout(() => {
        apiRef.current?.focus();
      }, 500);
      return;
    }

    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitting) {
    return (
      <Row className="justify-content-center py-5">
        <Col className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{`${t('Submitting')}...`}</span>
          </Spinner>
          <span className="ms-3">{`${t('Please wait')}...`}</span>
        </Col>
      </Row>
    );
  }
  if (submitted) {
    return (
      <Alert variant="success" className="my-3 d-flex-column justify-content-center">
        <RichText body={getFieldDataByName('success_message')?.text} />
      </Alert>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-md-center gx-3 gy-3 ">
        <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          {apiError && (
            <Alert variant="error" role="alert" tabIndex={0} ref={apiRef}>
              {apiError}
            </Alert>
          )}
          <Row className="gx-3 gy-3">
            <Col xs={12} md={6}>
              <Form.Group controlId="firstName" className="required">
                <Form.Label>{getFieldDataByName('first_name').title}</Form.Label>
                <Form.Control
                  {...register('firstName', {
                    required: validateRequiredFirstName(t),
                  })}
                  type="text"
                  maxLength={60}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.firstName && errors.firstName.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="lastName" className="required">
                <Form.Label>{getFieldDataByName('last_name').title}</Form.Label>
                <Form.Control
                  {...register('lastName', {
                    required: validateRequiredLastName(t),
                  })}
                  type="text"
                  maxLength={60}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.lastName && errors.lastName.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="petName">
                <Form.Label>{getFieldDataByName('pet_name').title}</Form.Label>
                <Form.Control {...register('petName')} type="text" maxLength={60} />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="petBirthday" className="required">
                <Form.Label>{getFieldDataByName('pet_birthday').title}</Form.Label>
                <Form.Control
                  {...register('petBirthday', {
                    required: validateRequired('Pet Birthday', t),
                    validate: validateDateRange('Pet Birthday', t),
                  })}
                  type="date"
                  min={minBirthDate}
                  max={maxBirthDate}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.petBirthday && errors.petBirthday.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            {petType === 'DOG' && (
                <Col xs={12} md={6}>
                  <Form.Group controlId="breedSize" className="required">
                    <Form.Label>{getFieldDataByName('breed_size').title}</Form.Label>
                    <Form.Select {...register('breedSize')}>
                      <option value="" />
                      {(getFieldDataByName('breed_size')?.options ?? []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.breedSize && errors.breedSize.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
            )}
            <Col xs={12} md={6}>
              <Form.Group controlId="petGoHomeDate" className="required">
                <Form.Label>{getFieldDataByName('pet_go_home_date').title}</Form.Label>
                <Form.Control
                    {...register('petGoHomeDate', {
                      required: validateRequired('Pet go home date', t),
                      validate: validateDateRange('Pet go home date', t),
                    })}
                    type="date"
                    min={minBirthDate}
                    max={maxBirthDate}
                    aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.petGoHomeDate && errors.petGoHomeDate.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="email" className="required">
                <Form.Label>{getFieldDataByName('email_address').title}</Form.Label>
                <Form.Control
                  {...register('email', {
                    required: validateRequiredEmail(t),
                    pattern: validateEmailPattern(t),
                  })}
                  type="email"
                  placeholder="example@gmail.com"
                  maxLength={128}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.email && errors.email.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="postalCode" className="required">
                <Form.Label>{getFieldDataByName('postal_code').title}</Form.Label>
                <Form.Control
                  {...register('postalCode', {
                    required: validateRequiredPostalCode(t),
                    pattern: validatePostalCodePattern(t),
                  })}
                  type="postalCode"
                  maxLength={128}
                  aria-required="true"
                  isInvalid={!!errors?.postalCode}
                  aria-invalid={!!errors?.postalCode}
                  required
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.postalCode && errors.postalCode.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          {formOptIn && (
            <Row className="gx-3 gy-3 mt-1">
              <Col xs={12}>
                <RichText body={formOptIn} />
              </Col>
            </Row>
          )}
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12}>
              <Form.Group controlId="agreed">
                <Form.Check typography-h2 type="checkbox">
                  <Form.Check.Input {...register('agreed')} type="checkbox" />
                  <Form.Check.Label aria-hidden="true">
                    {getFieldDataByName('disclaimer').text && (
                      <RichText body={getFieldDataByName('disclaimer').text} />
                    )}
                  </Form.Check.Label>
                </Form.Check>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12}>
              <Button
                className="justify-content-center mt-3"
                type="submit"
                size="sm"
                style={{ padding: '.5rem 3rem' }}
                disabled={submitting}
              >
                {submitting ? 'Please wait...' : getFieldDataByName('actions').submitLabel}
              </Button>
            </Col>
          </Row>
        </Form>
      </Row>
    </Container>
  );
};

export default BreederAdoptionForm;
